import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Page } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import RecordingDrawer from "../components/RecordingDrawer";
import { api, notifyErr, fmtDate, fmtSec } from "../lib/api";
import { useCustomers, useSelectedCustomer } from "../lib/hooks";
import { CategoryBadge, RecStatusBadge } from "../lib/labels";

export default function Registrazioni() {
  const { customers } = useCustomers();
  const [cid, setCid] = useSelectedCustomer(customers);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => cid && api.get(`/recordings?customer_id=${cid}&q=${encodeURIComponent(q)}&limit=300`).then((r) => setData(r.data)).catch(notifyErr);
  useEffect(() => { load(); setSel([]); // eslint-disable-next-line
  }, [cid]);
  const analyze = async (reanalyze) => {
    if (reanalyze && !window.confirm("La rianalisi riporta le opportunità già revisionate (anche approvate) allo stato «Da verificare». Continuare?")) return;
    try {
      const j = (await api.post("/analyze", { customer_id: cid, recording_ids: sel, reanalyze })).data;
      toast.success(`Analisi AI avviata su ${j.total} registrazioni`);
      setSel([]);
    } catch (e) { notifyErr(e); }
  };
  const delAudio = async (id) => {
    if (!window.confirm("Eliminare la copia audio acquisita? L'originale su AlFa Dial non viene toccato.")) return;
    try { await api.delete(`/recordings/${id}/audio`); toast.success("Copia audio eliminata"); load(); } catch (e) { notifyErr(e); }
  };
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <Page title="Registrazioni" subtitle="Tutte le registrazioni acquisite o caricate, con trascrizione e classificazione." testid="registrazioni-page"
      actions={<>
        <Button className="rounded-sm bg-[#0047FF] h-8" onClick={() => analyze(false)} data-testid="start-analysis-btn"><Sparkles size={14} className="mr-1" />Avvia analisi AI {sel.length ? `(${sel.length})` : "(non analizzate)"}</Button>
        <Button variant="outline" className="rounded-sm h-8" disabled={!sel.length} onClick={() => analyze(true)} data-testid="reanalyze-btn">Rianalizza selezionate</Button>
        <CustomerSelect customers={customers} value={cid} onChange={setCid} />
      </>}>
      <div className="flex gap-2 mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca telefono, lead, agente, campagna, file…" className="h-8 rounded-sm max-w-md" data-testid="recordings-search-input" />
        <Button variant="outline" className="h-8 rounded-sm" onClick={load} data-testid="recordings-search-btn">Cerca</Button>
        <span className="text-xs text-neutral-500 self-center" data-testid="recordings-total">{data.total} registrazioni</span>
      </div>
      <div className="bg-white border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead className="w-8" /><TableHead>Origine</TableHead><TableHead>Data chiamata</TableHead><TableHead>Telefono</TableHead><TableHead>Campagna</TableHead><TableHead>Agente</TableHead><TableHead>Esito</TableHead><TableHead className="text-right">Durata</TableHead><TableHead>Stato</TableHead><TableHead>Classificazione AI</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {data.items.map((r) => (
              <TableRow key={r.id} className="row-hover text-xs cursor-pointer" onClick={() => setOpen(r.id)} data-testid={`recording-row-${r.id}`}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`recording-select-${r.id}`} /></TableCell>
                <TableCell>{r.source === "upload" ? r.original_name : `AlFa Dial #${r.vicidial_recording_id}`}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(r.call_date)}</TableCell>
                <TableCell className="font-mono">{r.phone_number || "—"}</TableCell><TableCell className="font-mono">{r.campaign}</TableCell>
                <TableCell>{r.agent}</TableCell><TableCell className="font-mono">{r.disposition}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtSec(r.duration_sec)}</TableCell>
                <TableCell><RecStatusBadge status={r.status} /></TableCell>
                <TableCell>{r.analysis ? <CategoryBadge code={r.analysis.category} /> : "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>{r.has_audio && <Button size="sm" variant="ghost" className="h-6 text-red-600" title="Elimina copia audio" onClick={() => delAudio(r.id)} data-testid={`delete-audio-${r.id}`}><Trash2 size={13} /></Button>}</TableCell>
              </TableRow>
            ))}
            {!data.items.length && <TableRow><TableCell colSpan={11} className="text-center text-neutral-500 py-10">Nessuna registrazione per questo cliente.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <RecordingDrawer recordingId={open} onClose={() => setOpen(null)} onChanged={load} />
    </Page>
  );
}
